import { useState, useMemo, useCallback, useLayoutEffect } from 'react';
import { ModalBase, ModalHeader, ChooseBase, ModalFooter } from 'components';
import * as S from './ChooseCountries.styled';
import ChooseCountriesContent from './Content';
import { useFacade } from './useFacade';
import { manageAppsAPI } from 'services/manageApps.service';
import { useSignal } from '@preact/signals-react';

const checkName = (name, search) => {
  return name?.toLowerCase().includes(search?.toLowerCase());
};

export const ChooseCountries = ({ isOpen, title, selectedCountries, saveButtonAction, close }) => {
  const { data: all_countries } = manageAppsAPI.useGetCountriesListQuery();

  const isDirty$ = useSignal(true);

  const {
    state: selectedCountriesCCA2,
    isActive: isCountrySelected,
    toggle: toggleSelectedCountry,
    toggleOffAll: deselectAll,
  } = useFacade(selectedCountries);

  const {
    state: { length: countOfOpenedGroups },
    isActive: isGroupOpened,
    toggle: toggleOpenedGroup,
    toggleOffAll: collapseAllGroups,
    toggleOn: toggleGroupsOn,
    toggleOff: toggleGroupsOff,
  } = useFacade();

  const [search, setSearch] = useState('');
  const { length: countOfSelectedCountries } = selectedCountriesCCA2;
  const [countriesGroups] = useState(() => all_countries);

  const foundedCountriesGroups = useMemo(() => {
    if (!search) return countriesGroups;
    return Object.keys(countriesGroups).reduce(function (acc, group_name) {
      if (checkName(group_name, search)) {
        const countries = countriesGroups[group_name].filter(({ name }) => checkName(name, search));
        if (countries.length) {
          acc = { ...acc, [group_name]: [...countries] };
          toggleGroupsOn(group_name);
          return acc;
        } else {
          acc = { ...acc, [group_name]: [] };
          toggleGroupsOff(group_name);
          return acc;
        }
      } else if (!checkName(group_name, search)) {
        const countries = countriesGroups[group_name].filter(
          ({ name, cca2 }) => checkName(name, search) || checkName(cca2, search),
        );
        if (countries.length) {
          acc = { ...acc, [group_name]: [...countries] };
          toggleGroupsOn(group_name);
          return acc;
        } else {
          toggleGroupsOff(group_name);
          return acc;
        }
      }
      return null;
    }, {});
  }, [countriesGroups, search]);

  const handleSearchOnChange = (e) => {
    setSearch(e.target.value || '');
  };

  const isGroupChecked = useCallback(
    (countries) => {
      return countries?.map(({ cca2 }) => cca2).every(isCountrySelected);
    },
    [isCountrySelected],
  );

  const isSomeInGroupChecked = useCallback(
    (countries) => countries.map(({ cca2 }) => cca2).some(isCountrySelected),
    [isCountrySelected],
  );

  const toggleCountriesGroup = (countriesInGroup) => {
    const countriesInGroupCCA2 = countriesInGroup.map(({ cca2 }) => cca2);
    if (countriesInGroupCCA2.every(isCountrySelected)) {
      return toggleSelectedCountry(...countriesInGroupCCA2);
    }
    toggleSelectedCountry(...countriesInGroupCCA2.filter((cca2) => !isCountrySelected(cca2)));
  };
  useLayoutEffect(() => {
    if (search) {
      return toggleGroupsOn(...Object.keys(foundedCountriesGroups));
    }
    return Object.keys(foundedCountriesGroups).forEach((group_name) => {
      const somethingInGroupSelected = isSomeInGroupChecked(all_countries[group_name]);
      const action = somethingInGroupSelected ? toggleGroupsOn : toggleGroupsOff;
      action(group_name);
    });
  }, [countriesGroups, search]);
  const handleClearSearchValue = () => {
    setSearch('');
  };

  return (
    <>
      <ModalBase isOpen={isOpen}>
        <S.Box>
          <ModalHeader title={title} close={close} />
          <S.Content>
            <ChooseBase.OneSection
              showBtnDeselectAll={countOfSelectedCountries > 0}
              isGroupOpen={countOfOpenedGroups > 0}
              handleDeselectAll={deselectAll}
              handleCollapseAll={collapseAllGroups}
              searchValue={search}
              searchOnChange={handleSearchOnChange}
              handleClearSearchValue={handleClearSearchValue}
            >
              <ChooseCountriesContent
                all_countries={all_countries}
                countriesGroupsWithCountries={foundedCountriesGroups}
                toggleOpenedGroup={toggleOpenedGroup}
                isGroupOpened={isGroupOpened}
                toggleCountriesGroup={toggleCountriesGroup}
                isGroupChecked={isGroupChecked}
                isCountrySelected={isCountrySelected}
                toggleSelectedCountry={toggleSelectedCountry}
                search={search}
                isDirty$={isDirty$}
              />
            </ChooseBase.OneSection>
          </S.Content>
          <ModalFooter
            saveButtonText='Apply'
            saveButtonOnClick={() => {
              saveButtonAction(selectedCountriesCCA2);
              close();
            }}
            close={close}
            saveButtonDisabled={isDirty$.value}
          />
        </S.Box>
      </ModalBase>
    </>
  );
};

export default ChooseCountries;
